import { Divider, Flex } from "antd"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import styles from "../index.module.css"
import moment from "moment"
import React from "react"
import PropTypes from "prop-types"
import { getEventBorderColor, getEventColor, getJournalIcon, overlaps } from "../../../utils"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import EventBlock from "../EventBlock"
import Journal from "../../../Modals/Journal"
import { computeEventColumns, getEventPosition, HOUR_HEIGHT } from "../timeGridUtils"
import useDayGridData, { getResourceLabel, getResourceEvents } from "./useDayGridData"

const DayGrid = ({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour }) => {
  const {
    isJournalModalOpen, setIsJournalModalOpen, isBookingModalOpen, setIsBookingModalOpen,
    selectedBookingId, setSelectedBookingId, currentDay, hasJournalPermission, journalNoteDates,
    active, timeOffForDay, dayEvents, resources, totalHeight, gridTemplateColumns,
    isCrewTab, isEquipmentTab, handleBookingCancel, handleBookingCancelSuccess,
    refreshCalendarBookings,
  } = useDayGridData({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour })

  const displayDate = date ? moment(date).format("dddd, D MMMM") : ""
  const columnCount = resources.length || 1
  const showCurrentTimeLine = moment(date).isSame(moment(), "day")
  const currentTimeOffset = showCurrentTimeLine ? Math.max(0, ((moment().hour() - startHour) * 60 + moment().minute()) * (HOUR_HEIGHT / 60)) : null

  return (
    <>
      <Flex vertical className="overflow-auto" flex={1}>
        <Flex gap={8} align="center" className="pt-m px-s">
          <Text>{displayDate} Journal</Text>
          {hasJournalPermission && <IconWithBg icon={getJournalIcon(journalNoteDates, currentDay)} onClick={active ? () => setIsJournalModalOpen(true) : undefined} disabled={!active} />}
        </Flex>
        <Divider />
        <div key={`grid-${columnCount}`} className={styles.timeGridRoot}>
          <div className={styles.timeGridHeaderRow} style={{ gridTemplateColumns }}>
            <div className={styles.timeLabelHeader} />
            <div className={styles.timeDividerHeader} />
            {resources.map((resource) => {
              const label = getResourceLabel(resource)
              const key = typeof resource === "string" ? resource : resource?.id ?? label
              return <div key={key} className={styles.resourceHeaderCell}><Text fw="500">{label}</Text></div>
            })}
          </div>
          <div className={styles.timeGridTable} style={{ display: "grid", gridTemplateRows: `repeat(${hours.length}, ${HOUR_HEIGHT}px)`, gridTemplateColumns, position: "relative" }}>
            {showCurrentTimeLine && currentTimeOffset !== null && (
              <div className={styles.currentTimeLine} style={{ top: currentTimeOffset, gridColumn: "1 / -1", position: "absolute", zIndex: 6 }}>
                <span className={styles.currentTimeDot} />
              </div>
            )}
            {hours.map((hour, hIdx) => {
              const now = moment()
              const dayMoment = moment(date)
              const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())
              return (
                <div key={`label-${hour}`} className={`${styles.timeLabel} ${isPastHour ? styles.pastCalendarCell : ""}`} style={{ gridRow: hIdx + 1, gridColumn: 1 }}>
                  <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                </div>
              )
            })}
            <div style={{ gridRow: `1 / span ${hours.length}`, gridColumn: 2, background: "var(--color-surface3)", width: 1 }} />
            {resources.map((resource, resIdx) => {
              const resourceId = typeof resource === "string" ? null : resource?.id
              const resourceKey = typeof resource === "string" ? resource : resource?.id ?? getResourceLabel(resource)
              const resEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId)
              const eventsWithCols = computeEventColumns(resEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid()))
              return (
                <div key={resourceKey} className={styles.dayColumn} style={{ gridRow: `1 / span ${hours.length}`, gridColumn: resIdx + 3, height: totalHeight, position: "relative" }}>
                  {hours.map((hour, hIdx) => {
                    const isPastHour = moment(date).isBefore(moment(), "day") || (moment(date).isSame(moment(), "day") && Number(hour) < moment().hour())
                    return <div key={`divider-${hour}`} className={styles.hourDivider} style={{ top: hIdx * HOUR_HEIGHT, ...(isPastHour ? { background: "var(--color-surface2)" } : {}) }} />
                  })}
                  {timeOffForDay?.allDay && <div className={`${styles.timeOffOverlay} ${styles.timeOffOverlayFull}`} />}
                  {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).map((toff) => {
                    const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
                    const botPx = Math.max(0, (toff.end.hour() - startHour) * 60 + toff.end.minute()) * (HOUR_HEIGHT / 60)
                    return <div key={`${currentDay}-${resourceKey}-${toff.start.format("HH:mm")}`} className={styles.timeOffOverlay} style={{ top: topPx, height: Math.max(0, botPx - topPx) }} />
                  })}
                  {eventsWithCols.map((ev) => {
                    const pos = getEventPosition(ev, startHour)
                    if (!pos) return null
                    const colW = 100 / ev.totalCols
                    const conflict = timeOffForDay?.allDay || (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
                    return (
                      <div key={ev.id ?? ev.title + ev.start} className={styles.eventBlockPositioned} style={{ top: pos.top, height: pos.height, left: `${ev.colIndex * colW}%`, width: `${colW}%` }}>
                        <EventBlock event={ev} eventColor={getEventColor(ev.color)} eventBorderColor={getEventBorderColor()} conflict={conflict} setIsBookingModalOpen={setIsBookingModalOpen} setSelectedBookingId={setSelectedBookingId} fullHeight />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </Flex>
      {isJournalModalOpen && <Journal open={isJournalModalOpen} setOpen={setIsJournalModalOpen} onCancel={() => setIsJournalModalOpen(false)} selectedDate={date} startDate={date} endDate={date} />}
      {isBookingModalOpen && <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} onCancelSuccess={handleBookingCancelSuccess} onAssignmentChange={refreshCalendarBookings} />}
    </>
  )
}

DayGrid.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  resources: PropTypes.array,
  activeTab: PropTypes.string,
  profileIds: PropTypes.array,
  boatIds: PropTypes.array,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
}

export default DayGrid
